import { useState, useEffect, useMemo } from 'react'
import { ChevronRight, Trash2, RefreshCw } from 'lucide-react'
import { useMatch, useAddMatchPlayer, useRemoveMatchPlayer, useDeleteMatch, useUpdateMatchPlayer, useUpdateMatch } from '../hooks/useMatches'
import { useBatchRefreshHandicaps } from '../hooks/usePlayers'
import { useMatchGames } from '../hooks/useGames'
import { useAuth } from '../contexts/AuthContext'
import { getInitials } from '../utils/colors'
import { hasHandicapIndex, formatHandicapIndex, splitHandicapForInput, combineHandicapInput } from '../utils/handicaps'
import PlusHandicapToggle from './shared/PlusHandicapToggle'
import { getMatchTypeLabels } from '../utils/matchTypeLabels'
import { usePlayerSearch } from '../hooks/usePlayerSearch'
import RoundSetupWizard from './RoundSetupWizard'
import { useWizardPersistence } from '../hooks/useWizardPersistence'
import Spinner from './Spinner'
import ManualPlayerForm from './shared/ManualPlayerForm'
import PlayerSearchInput from './shared/PlayerSearchInput'
import PlayerSearchResults from './shared/PlayerSearchResults'
import { onlineManager } from '@tanstack/react-query'
import logger from '../utils/logger'

export default function QuickRoundSetup({ matchId, onBack, onRoundCreated, onContinueToTeamSetup, onContinueToDashboard }) {
  const { user } = useAuth()
  const { data: match, isLoading: matchLoading } = useMatch(matchId)
  const { data: matchGames } = useMatchGames(matchId)
  const addMatchPlayer = useAddMatchPlayer()
  const removeMatchPlayer = useRemoveMatchPlayer()
  const deleteMatch = useDeleteMatch()
  const updateMatch = useUpdateMatch()

  const [phase, setPhase] = useState(() => {
    try {
      const raw = localStorage.getItem(`press_wizard_${matchId}`)
      if (raw) {
        const parsed = JSON.parse(raw)
        if (parsed?.phase === 'round-setup') return 'round-setup'
      }
    } catch { /* ignore */ }
    return 'players'
  }) // 'players' | 'round-setup'
  const [searchText, setSearchText] = useState('')
  const [showManualForm, setShowManualForm] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [editingHandicap, setEditingHandicap] = useState(null) // { id, magnitude, isPlus }
  const [setupName, setSetupName] = useState(() => {
    try {
      const saved = localStorage.getItem(`press_setup_name_${matchId}`)
      if (saved != null) return saved
    } catch { /* ignore */ }
    return ''
  })
  const updateMatchPlayer = useUpdateMatchPlayer()
  const batchRefresh = useBatchRefreshHandicaps()

  const { savedState, saveState, clearState } = useWizardPersistence(matchId)

  // Persist setupName to localStorage on every change
  useEffect(() => {
    if (setupName == null) return
    try { localStorage.setItem(`press_setup_name_${matchId}`, setupName) } catch { /* ignore */ }
  }, [setupName, matchId])

  // Listen for Android back gesture triggering discard
  useEffect(() => {
    const handler = () => setShowDeleteConfirm(true)
    window.addEventListener('press:back-discard', handler)
    return () => window.removeEventListener('press:back-discard', handler)
  }, [])

  const matchPlayers = match?.match_players || []
  const matchType = match?.match_type || 'quick_round'

  // Players already in the match (by player_id)
  const matchPlayerIds = useMemo(() => new Set(matchPlayers.map((mp) => mp.player_id)), [matchPlayers])

  const {
    matchingFollowedPlayers,
    localSearchResults,
    matchingGroups,
    followedPlayerIds,
    allPlayers,
    myFollows,
    isSearching,
  } = usePlayerSearch({ excludeIds: matchPlayerIds, searchText })

  if (matchLoading) {
    return <Spinner message="Loading..." />
  }

  const isOwner = match?.owner_id === user?.id
  const isTrip = matchType === 'trip' || matchType === 'invitational'
  const maxPlayers = matchType === 'quick_round' ? 4 : (matchType === 'team_match' || matchType === 'invitational') ? Infinity : 8
  const labels = getMatchTypeLabels(matchType)
  const atPlayerLimit = matchPlayers.length >= maxPlayers
  const rounds = matchGames || []

  const handleAddExistingPlayer = (playerId) => {
    addMatchPlayer.mutate({ match_id: matchId, player_id: playerId })
  }

  const handleRemovePlayer = (matchPlayerId) => {
    removeMatchPlayer.mutate({ id: matchPlayerId, match_id: matchId })
  }

  const handleHandicapSave = (matchPlayerId) => {
    if (!editingHandicap) return
    const val = combineHandicapInput({ isPlus: editingHandicap.isPlus, magnitude: editingHandicap.magnitude })
    if (val == null) {
      setEditingHandicap(null)
      return
    }
    updateMatchPlayer.mutate(
      { id: matchPlayerId, match_id: matchId, handicap_index: val },
      { onSuccess: () => setEditingHandicap(null) }
    )
  }

  const handleBackFromPlayers = () => {
    // Show confirmation before deleting empty match
    if (rounds.length === 0) {
      setShowDeleteConfirm(true)
    } else {
      onBack()
    }
  }

  const handleAddGroup = (group) => {
    const members = group.members || []
    const toAdd = members
      .filter((m) => m.player_id && !matchPlayerIds.has(m.player_id))
      .map((m) => m.player_id)
    if (toAdd.length === 0) return
    const limit = maxPlayers - matchPlayers.length
    const adding = toAdd.slice(0, limit)
    adding.forEach((playerId) => {
      addMatchPlayer.mutate({ match_id: matchId, player_id: playerId })
    })
  }

  // Phase 2: Round Setup
  if (phase === 'round-setup') {
    return (
      <RoundSetupWizard
        matchId={matchId}
        embedded={true}
        onClose={() => {
          saveState({ phase: 'players' })
          setPhase('players')
        }}
        onRoundCreated={(game) => {
          clearState()
          onRoundCreated?.(game)
        }}
        initialState={savedState}
      />
    )
  }

  // Phase 1: Players
  return (
    <div style={{ paddingBottom: '6rem' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '1.5rem',
      }}>
        <h1 style={{ fontSize: '1.25rem', fontWeight: '700' }}>
          {isTrip ? `${matchType === 'invitational' ? 'Invitational' : 'Golf Trip'} Setup` : labels.singular}
        </h1>
        <button
          onClick={handleBackFromPlayers}
          disabled={deleteMatch.isPending}
          style={{
            width: '36px',
            height: '36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '9999px',
            color: '#ef4444',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            opacity: deleteMatch.isPending ? 0.5 : 1,
            flexShrink: 0,
          }}
        >
          <Trash2 size={16} />
        </button>
      </div>

      {/* Name input for trips/invitationals */}
      {isTrip && (
        <div style={{ marginBottom: '1rem' }}>
          <h3 style={{
            fontSize: '0.9375rem',
            fontWeight: '700',
            color: 'var(--text-primary)',
            marginBottom: '0.5rem',
          }}>
            {matchType === 'invitational' ? 'Invitational Name' : 'Trip Name'}
          </h3>
          <input
            value={setupName}
            onChange={(e) => setSetupName(e.target.value)}
            onBlur={() => {
              const trimmed = setupName.trim()
              if (trimmed && trimmed !== match.match_name) {
                updateMatch.mutate({ id: matchId, match_name: trimmed })
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.target.blur()
            }}
            placeholder={matchType === 'invitational' ? 'e.g. The Ryder Cup Classic' : 'e.g. Myrtle Beach 2026'}
            maxLength={60}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              borderRadius: '0.5rem',
              border: '1px solid var(--border-color)',
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              fontSize: '1.125rem',
              fontWeight: '600',
              outline: 'none',
            }}
          />
        </div>
      )}

      {(matchType === 'team_match' || matchType === 'invitational') && (
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.8125rem', lineHeight: 1.5, marginBottom: '1rem' }}>
          Search for and add everyone who will be participating in the event. You'll assign teams and set up games in the next steps.
        </p>
      )}

      {/* Current match players */}
      <div style={{
        backgroundColor: 'var(--bg-secondary)',
        borderRadius: '0.75rem',
        border: '1px solid var(--border-color)',
        padding: '1rem',
        marginBottom: '1rem',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'baseline',
          marginBottom: '0.75rem',
        }}>
          <h3 style={{
            flex: 1,
            fontSize: '0.75rem',
            fontWeight: '600',
            color: 'var(--text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}>
            Players in Match ({matchPlayers.length}{maxPlayers !== Infinity ? `/${maxPlayers}` : ''})
          </h3>
          <span style={{
            fontSize: '0.625rem',
            fontWeight: '600',
            color: 'var(--text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginRight: isOwner ? '28px' : 0,
            paddingRight: isOwner ? '0.625rem' : 0,
          }}>
            HCP Index
          </span>
        </div>

        {matchPlayers.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', textAlign: 'center', padding: '0.5rem 0' }}>
            No players yet
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            {matchPlayers.map((mp) => {
              const player = mp.player
              if (!player) return null
              const isMatchOwner = mp.user_id != null && mp.user_id === match?.owner_id

              return (
                <div
                  key={mp.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.625rem',
                    padding: '0.5rem 0.25rem',
                    borderBottom: '1px solid var(--border-color)',
                  }}
                >
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: player.avatar_color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '700',
                    fontSize: '0.75rem',
                    color: '#fff',
                    flexShrink: 0,
                  }}>
                    {getInitials(player)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                      {player.first_name || player.last_name
                        ? `${player.first_name || ''} ${player.last_name || ''}`.trim()
                        : player.display_name}
                    </span>
                    {isMatchOwner && (
                      <span style={{
                        marginLeft: '0.5rem',
                        fontSize: '0.625rem',
                        fontWeight: '600',
                        color: 'var(--accent-primary)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}>
                        You
                      </span>
                    )}
                  </div>
                  {isOwner && editingHandicap?.id === mp.id ? (
                    <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center', flexShrink: 0 }}>
                      <PlusHandicapToggle
                        compact
                        isPlus={editingHandicap.isPlus}
                        onChange={(next) => setEditingHandicap({ ...editingHandicap, isPlus: next })}
                      />
                      <input
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.1"
                        value={editingHandicap.magnitude}
                        onChange={(e) => setEditingHandicap({ ...editingHandicap, magnitude: e.target.value })}
                        onBlur={() => handleHandicapSave(mp.id)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleHandicapSave(mp.id) }}
                        autoFocus
                        style={{
                          width: '52px',
                          textAlign: 'center',
                          padding: '0.125rem 0.25rem',
                          borderRadius: '0.25rem',
                          fontSize: '0.8125rem',
                          fontWeight: '600',
                          backgroundColor: 'var(--bg-secondary)',
                          border: '1px solid var(--accent-primary)',
                          color: 'var(--text-primary)',
                          flexShrink: 0,
                        }}
                      />
                    </div>
                  ) : (
                    <button
                      onClick={() => isOwner && setEditingHandicap({ id: mp.id, ...splitHandicapForInput(mp.handicap_index ?? 0) })}
                      style={{
                        width: '52px',
                        textAlign: 'center',
                        padding: '0.125rem 0.25rem',
                        borderRadius: '9999px',
                        fontSize: '0.8125rem',
                        fontWeight: '600',
                        backgroundColor: 'var(--bg-secondary)',
                        border: '1px solid var(--border-color)',
                        color: 'var(--text-primary)',
                        flexShrink: 0,
                        cursor: isOwner ? 'pointer' : 'default',
                      }}
                    >
                      {formatHandicapIndex(mp.handicap_index || 0)}
                    </button>
                  )}
                  {isOwner && !isMatchOwner && (
                    <button
                      onClick={() => handleRemovePlayer(mp.id)}
                      style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '9999px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#ef4444',
                        flexShrink: 0,
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                  {isOwner && isMatchOwner && (
                    <div style={{ width: '28px', flexShrink: 0 }} />
                  )}
                </div>
              )
            })}
          </div>
        )}
        {/* Refresh GHIN Handicaps */}
        {isOwner && matchPlayers.length > 0 && (() => {
          const ghinPlayers = matchPlayers.filter((mp) => mp.player?.ghin_number)
          const hasGhin = ghinPlayers.length > 0
          return (
            <button
              onClick={() => {
                if (!hasGhin) return
                batchRefresh.mutate(ghinPlayers.map((mp) => ({
                  ...mp.player,
                  match_player_id: mp.id,
                })))
              }}
              disabled={batchRefresh.isPending || !hasGhin}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.375rem',
                width: '100%',
                padding: '0.625rem 1rem',
                marginTop: '0.5rem',
                borderRadius: '9999px',
                fontSize: '0.8125rem',
                fontWeight: '600',
                color: !hasGhin ? 'var(--text-muted)' : batchRefresh.isPending ? 'var(--text-muted)' : 'var(--accent-secondary, #f59e0b)',
                backgroundColor: 'var(--bg-tertiary)',
                border: '1px dashed var(--border-color)',
                opacity: !hasGhin || batchRefresh.isPending ? 0.5 : 1,
              }}
            >
              <RefreshCw size={14} style={batchRefresh.isPending ? { animation: 'spin 1s linear infinite' } : {}} />
              {!hasGhin
                ? 'Sync Latest GHIN Handicaps'
                : batchRefresh.isPending
                  ? 'Syncing...'
                  : batchRefresh.isSuccess
                    ? `Updated ${batchRefresh.data?.updated || 0} handicap${batchRefresh.data?.updated === 1 ? '' : 's'}`
                    : `Sync Latest GHIN Handicaps (${ghinPlayers.length})`
              }
            </button>
          )
        })()}
      </div>

      {/* Player limit message */}
      {atPlayerLimit && (
        <div style={{
          padding: '0.625rem 1rem',
          marginBottom: '1rem',
          borderRadius: '0.5rem',
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          border: '1px solid rgba(245, 158, 11, 0.25)',
          fontSize: '0.8125rem',
          color: 'var(--text-secondary)',
          textAlign: 'center',
        }}>
          {labels.singular} is limited to {maxPlayers} players
        </div>
      )}

      {isOwner && matchPlayers.length > 0 && (
        <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginBottom: '0.75rem' }}>
          Tap a player's handicap index to adjust it for this match only.
        </p>
      )}

      {/* Add Players heading + Manual add form */}
      {!atPlayerLimit && (
        <h2 style={{ fontSize: '1.125rem', fontWeight: '700', color: 'var(--accent-primary)', marginBottom: '0.75rem' }}>
          Add Players
        </h2>
      )}
      {!atPlayerLimit && (
        <>
          <div style={{ marginBottom: '1.5rem' }}>
            <ManualPlayerForm
              onPlayerCreated={(newPlayer) => {
                addMatchPlayer.mutate({ match_id: matchId, player_id: newPlayer.id })
              }}
              excludeIds={matchPlayerIds}
              allPlayers={allPlayers}
              collapsed={!showManualForm}
              onToggle={() => setShowManualForm((v) => !v)}
              onDedupAdd={handleAddExistingPlayer}
            />
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            marginBottom: '1rem',
          }}>
            <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border-color)' }} />
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: '500' }}>or</span>
            <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border-color)' }} />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <PlayerSearchInput value={searchText} onChange={setSearchText} />
          </div>
        </>
      )}

      {!atPlayerLimit && (
        <PlayerSearchResults
          matchingFollowedPlayers={matchingFollowedPlayers}
          localSearchResults={localSearchResults}
          matchingGroups={matchingGroups}
          followedPlayerIds={followedPlayerIds}
          myFollows={myFollows}
          onAddPlayer={handleAddExistingPlayer}
          onAddGroup={handleAddGroup}
          isSearching={isSearching}
          addPending={addMatchPlayer.isPending}
          excludeIds={matchPlayerIds}
        />
      )}

      {/* Fixed bottom bar */}
      <div style={{
        position: 'fixed',
        bottom: 'var(--keyboard-height, 0px)',
        left: 0,
        right: 0,
        padding: '0.75rem 1rem',
        paddingBottom: 'var(--action-bar-pad-bottom)',
        backgroundColor: 'var(--bg-primary)',
        borderTop: '1px solid var(--border-color)',
        zIndex: 101,
      }}>
        {(() => {
          const needsName = isTrip && !setupName.trim()
          const needsPlayers = matchPlayers.length < 2
          const hint = needsName
            ? { text: `Add a ${matchType === 'invitational' ? 'Invitational' : 'Trip'} name to continue`, color: '#ef4444' }
            : needsPlayers
            ? { text: 'Add players to continue', color: '#ef4444' }
            : { text: 'Confirm handicaps before continuing', color: '#5e9be6' }
          const canContinue = matchPlayers.length >= 2 && (!isTrip || setupName.trim())
          return (
            <>
              <p style={{ textAlign: 'center', fontSize: '0.75rem', color: hint.color, marginBottom: '0.375rem' }}>
                {hint.text}
              </p>
              <button
                onClick={() => {
                  // Flush name for trips/invitationals
                  if (isTrip) {
                    const trimmed = setupName.trim()
                    if (trimmed && trimmed !== match.match_name) {
                      updateMatch.mutate({ id: matchId, match_name: trimmed })
                    }
                    try { localStorage.removeItem(`press_setup_name_${matchId}`) } catch { /* ignore */ }
                  }
                  if (matchType === 'team_match' || matchType === 'invitational') {
                    saveState({ phase: 'team-setup' })
                    onContinueToTeamSetup?.()
                  } else if (matchType === 'trip') {
                    clearState()
                    onContinueToDashboard?.()
                  } else {
                    saveState({ phase: 'round-setup' })
                    setPhase('round-setup')
                  }
                }}
                disabled={!canContinue}
                className="btn-primary"
                style={{
                  width: '100%',
                  height: '48px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  borderRadius: '9999px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  opacity: canContinue ? 1 : 0.5,
                }}
              >
                {matchType === 'team_match' || matchType === 'invitational'
                  ? 'Continue to Team Setup'
                  : matchType === 'trip'
                  ? 'Activate Trip'
                  : 'Continue to Round Setup'}
                <ChevronRight size={20} />
              </button>
            </>
          )
        })()}
      </div>

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div
          onClick={() => setShowDeleteConfirm(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '1rem',
              border: '1px solid var(--border-color)',
              padding: '1.5rem',
              maxWidth: '340px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
            }}
          >
            <p style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '0.5rem', textAlign: 'center' }}>
              Discard {labels.singular}?
            </p>
            <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.4, marginBottom: '1.25rem', textAlign: 'center' }}>
              This cannot be undone.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <button
                onClick={() => {
                  if (!onlineManager.isOnline()) {
                    logger.capture(new Error('Round discard blocked — offline'), {
                      matchId, matchType: match?.match_type, source: 'QuickRoundSetup',
                    })
                    alert('Unable to discard — no network connection. Please try again when connectivity is restored.')
                    return
                  }
                  deleteMatch.mutate(matchId, {
                    onSuccess: () => {
                      setShowDeleteConfirm(false)
                      clearState()
                      try { localStorage.removeItem(`press_setup_name_${matchId}`) } catch { /* ignore */ }
                      onBack()
                    },
                    onError: (err) => {
                      logger.capture(new Error('Round discard failed: ' + err?.message), {
                        matchId, matchType: match?.match_type, source: 'QuickRoundSetup',
                        isOnline: onlineManager.isOnline(),
                      })
                      alert('Failed to discard. Please check your connection and try again.')
                    },
                  })
                }}
                disabled={deleteMatch.isPending}
                style={{
                  height: '44px',
                  width: '100%',
                  fontSize: '0.9375rem',
                  fontWeight: '600',
                  borderRadius: '9999px',
                  color: '#fff',
                  backgroundColor: '#ef4444',
                  border: 'none',
                  cursor: 'pointer',
                  opacity: deleteMatch.isPending ? 0.6 : 1,
                }}
              >
                {deleteMatch.isPending ? 'Discarding...' : 'Discard'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleteMatch.isPending}
                style={{
                  height: '44px',
                  width: '100%',
                  fontSize: '0.9375rem',
                  borderRadius: '9999px',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-secondary)',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
